import type { NotificationCategory, NotificationViewAs } from '@/features/notifications/api';

/**
 * Where a notification opens, read off the prototype's `opens` for its type: the order, on the
 * tab the destination names, or the client's wardrobe.
 *
 * "Order detail · Shipping" opens the order's Shipping & returns tab (ALS-30), and "· Payments"
 * its Instalments & invoicing tab (ALS-28). The daily Xero check's opens Reports, Xero sync
 * (ALS-388). Nothing opens a page that is not built.
 */
export type NotificationOpens =
    'order' | 'items' | 'shipping' | 'payments' | 'wardrobe' | 'xero-sync';

export interface NotificationType {
    key: string;
    /** The title's first segment, the prototype's `event`. */
    label: string;
    category: NotificationCategory;
    opens: NotificationOpens;
}

/**
 * The types the API raises, in the prototype's catalogue order (`data.js notificationTypes`), so
 * the Type filter reads as a vocabulary. Four of the Payments five are ALS-73's; Payment link
 * expired has no event, since links live in Xero and do not expire (ALS-385). "Return received" has
 * no status to fire it (G-055). Three are not in the prototype. Shipment exception is the alert ALS-34
 * asked for, carrying DHL's reason, and it sits beside DHL label failed. Part payment is Instalment
 * paid on an order with no plan (ALS-73), so it sits beside it. Xero out of sync is the daily Xero
 * check's (ALS-388), the whole team's, so it is never under Mine. No Time-based type is here, so
 * none can appear anywhere: MVP scope is Event only.
 */
export const NOTIFICATION_TYPES: readonly NotificationType[] = [
    { key: 'new-order', label: 'New order created', category: 'Orders', opens: 'order' },
    { key: 'products-secured', label: 'Products secured', category: 'Orders', opens: 'items' },
    { key: 'products-received', label: 'Products received', category: 'Orders', opens: 'items' },
    { key: 'order-shipped', label: 'Order shipped', category: 'Orders', opens: 'shipping' },
    { key: 'order-delivered', label: 'Order delivered', category: 'Orders', opens: 'shipping' },
    { key: 'order-cancelled', label: 'Order cancelled', category: 'Orders', opens: 'order' },
    { key: 'deposit-paid', label: 'Deposit paid', category: 'Payments', opens: 'payments' },
    { key: 'instalment-paid', label: 'Instalment paid', category: 'Payments', opens: 'payments' },
    { key: 'part-payment', label: 'Part payment', category: 'Payments', opens: 'payments' },
    { key: 'paid-in-full', label: 'Paid in full', category: 'Payments', opens: 'payments' },
    {
        key: 'bank-transfer',
        label: 'Bank transfer received',
        category: 'Payments',
        opens: 'payments',
    },
    { key: 'return-requested', label: 'Return requested', category: 'Returns', opens: 'order' },
    {
        key: 'refund-in-progress',
        label: 'Refund in progress',
        category: 'Returns',
        opens: 'payments',
    },
    { key: 'refund-completed', label: 'Refund completed', category: 'Returns', opens: 'payments' },
    { key: 'dhl-label-failed', label: 'DHL label failed', category: 'General', opens: 'shipping' },
    {
        key: 'shipment-exception',
        label: 'Shipment exception',
        category: 'General',
        opens: 'shipping',
    },
    {
        key: 'wardrobe-upload',
        label: 'Wardrobe self-upload',
        category: 'General',
        opens: 'wardrobe',
    },
    {
        key: 'xero-out-of-sync',
        label: 'Xero out of sync',
        category: 'General',
        opens: 'xero-sync',
    },
];

export const NOTIFICATION_TYPE_KEYS = NOTIFICATION_TYPES.map((type) => type.key);

export function notificationType(key: string): NotificationType | undefined {
    return NOTIFICATION_TYPES.find((type) => type.key === key);
}

export type NotificationTab = 'All' | Exclude<NotificationCategory, 'General'>;

/**
 * The tabs, All first. A tab shows only once a type of its own exists, so Payments waits for
 * ALS-73 rather than being a tab that can never hold anything.
 */
export const NOTIFICATION_TABS: readonly NotificationTab[] = (
    ['All', 'Orders', 'Payments', 'Returns'] as const
).filter((tab) => tab === 'All' || NOTIFICATION_TYPES.some((type) => type.category === tab));

export const VIEW_AS_OPTIONS: readonly { value: NotificationViewAs; label: string }[] = [
    { value: 'All', label: 'View as: all' },
    { value: 'Assignee', label: 'Assignee' },
    { value: 'Rm', label: 'Relationship manager' },
];

/** Every word the page and the card say, verbatim from the prototype where it says them. */
export const COPY = {
    page: {
        breadcrumb: 'Dashboard / Notifications',
        title: 'Notifications',
        subtitle: 'Everything that has happened across orders, payments and returns.',
    },
    tabs: { All: 'All', Orders: 'Orders', Payments: 'Payments', Returns: 'Returns' } as Record<
        NotificationTab,
        string
    >,
    scope: { team: 'Team', mine: 'Mine' },
    filters: {
        user: 'Filter by user',
        userAll: 'User: All',
        userAllOption: 'All',
        userSearch: 'Search users…',
        viewAs: 'View as',
        type: 'Filter by notification type',
        typeAll: 'Type: All',
        typeAllOption: 'All types',
        typeSearch: 'Search notification types…',
        clear: 'Clear filters',
    },
    unreadOf: (unread: number, everything: number) =>
        `${unread} unread of ${everything} notification${everything === 1 ? '' : 's'}`,
    markAllRead: 'Mark all read',
    showing: (from: number, to: number, total: number) =>
        total
            ? `Showing ${from}–${to} of ${total} notification${total === 1 ? '' : 's'}`
            : 'Showing 0 notifications',
    days: { today: 'Today', yesterday: 'Yesterday', earlier: 'Earlier' },
    unread: 'unread',
    /** Said of a notification whose order has been cancelled since. */
    cancelled: 'Cancelled',
    /** The reference a client's own notification carries where an order's number would be. */
    wardrobeRef: 'Wardrobe',
    /** The reference the daily Xero check's notification carries (ALS-388). */
    xeroRef: 'Xero',
    /** What the daily Xero check found new, its notification's detail (ALS-388). */
    differences: (n: number) => `${n} new ${n === 1 ? 'difference' : 'differences'}`,
    items: (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`,
    fraction: (reached: number, of: number) => `${reached} of ${of}`,
    empty: {
        filtered: {
            title: 'No notifications match these filters',
            body: (everything: number) =>
                `Try a wider type, another user, or clear the filters to see all ${everything}.`,
            action: 'Clear filters',
        },
        firstRun: {
            title: 'Nothing needs attention',
            body: 'New orders, payments awaiting confirmation and returns will appear here.',
        },
        Orders: {
            title: 'No order updates',
            body: 'New orders and status changes will appear here. Other activity is still under All.',
        },
        Payments: {
            title: 'No payment activity',
            body: 'Deposits, instalments and settled invoices will appear here. Other activity is still under All.',
        },
        Returns: {
            title: 'No returns to action',
            body: 'Returns needing attention will show here. Other activity is still under All.',
        },
        mine: {
            title: 'Nothing assigned to you',
            body: 'Notifications assigned to you will appear here. Switch to Team to see everything.',
        },
    },
    failed: {
        title: 'The notifications could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
    card: {
        title: 'Notifications',
        viewAll: 'View all',
    },
};
